import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useInView } from "react-intersection-observer";
import styled from "styled-components/macro";
import { useSWRInfinite } from "swr";
import { SearchBar } from "../SearchBar/SearchBar";
import { Loader } from "../Loader/Loader";
import { CheckBoxNoLabel } from "../CheckBoxes/CheckBoxes";
import { CheckBoxFinePrintLabel } from "../Form/Form";
import {
  AnimatedExpandToggle,
  FilterGroupHeader,
  FilterGroupWrapper,
  ScrollUL as FilterGroupOptionsList,
  SearchBarWrapper as FilterGroupSearchBarWrapper,
} from "../Filters/FilterGroup";
import { TextSmMedium, TextSmRegular } from "../Typography/TypographyV2";
import { useDebounce } from "../../util/hooks";
import { fetcher, useStoreState } from "../../util/util";
import { endpoints } from "../../endpoints";
import type {
  AdvancedFilterCard,
  AdvancedFilterCollectionInstance,
  AdvancedFilterKeyAttributeValuesResponse,
} from "../../types/types";

const VALUES_PAGE_SIZE = 20;
const VALUES_SEARCH_DEBOUNCE_MS = 400;

const PaneWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 296px;
  flex-shrink: 0;
  background: ${({ theme }) => theme.resolve("color_bg_surface_standard")};
  border-right: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.resolve("color_border_lighter")};
  overflow-y: auto;
`;

// z-index keeps the sticky bar above the list's `position: relative` checkboxes.
const SearchBarWrapper = styled.div`
  padding: ${({ theme }) => theme.resolve("spacing_lg")}
    ${({ theme }) => theme.resolve("spacing_xl")};
  border-bottom: ${({ theme }) => theme.resolve("border_filterGroup")} solid
    ${({ theme }) => theme.resolve("color_filterSearchBar_border")};
  background: ${({ theme }) => theme.resolve("color_filterSearchBar_bg")};
  position: sticky;
  top: 0;
  z-index: 1;
`;

const GroupHeaderButton = styled(FilterGroupHeader)`
  border: none;
  color: inherit;
  font-family: inherit;
  text-align: left;
`;

const ValuesSection = styled.div`
  padding-top: ${({ theme }) => theme.resolve("spacing_xs")};
  background: ${({ theme }) => theme.resolve("color_filterGroup_bg_expanded")};
`;

const ValueLabel = styled(CheckBoxFinePrintLabel)`
  display: flex;
  justify-content: space-between;
  gap: ${({ theme }) => theme.resolve("spacing_md")};
  width: 100%;
`;

const EmptyState = styled.div`
  padding: ${({ theme }) => theme.resolve("spacing_sm")}
    ${({ theme }) => theme.resolve("spacing_xl")};
`;

/**
 * Lazily fetches one collection instance's Key Attribute values (with
 * product counts), paginated + searchable via the values endpoint (BE plan
 * §3b). Uses a hand-rolled `useSWRInfinite` key rather than
 * `constructInfiniteScrollQuery` — that helper assumes a `{data,pagination}`
 * response shape, while this endpoint returns `{values,total}` (confirmed
 * shape, see the FE plan §9d) — so offset is tracked from `index` directly
 * instead of from the previous page's echoed offset.
 */
function useKeyAttributeValues(
  storefrontId: string,
  instanceId: string,
  query: string
) {
  const getKey = (
    index: number,
    previousPageData: AdvancedFilterKeyAttributeValuesResponse | null
  ) => {
    if (previousPageData && previousPageData.values.length === 0) return null;
    const offset = index * VALUES_PAGE_SIZE;
    const params = new URLSearchParams({
      offset: String(offset),
      limit: String(VALUES_PAGE_SIZE),
    });
    if (query) params.set("q", query);
    const baseUrl =
      endpoints.v2_storefronts_id_advancedFilters_collectionInstances_id_values(
        storefrontId,
        instanceId
      );
    return `${baseUrl}?${params}#select-scope=${instanceId}`;
  };

  const { data, error, size, setSize, isValidating } =
    useSWRInfinite<AdvancedFilterKeyAttributeValuesResponse>(getKey, fetcher);

  const values = useMemo(
    () => data?.flatMap((page) => page.values) ?? [],
    [data]
  );
  const total = data?.[data.length - 1]?.total ?? 0;
  const hasMore = values.length < total;
  const isLoading = !data && !error;

  return {
    values,
    isLoading,
    hasMore,
    loadMore: () => !isValidating && void setSize(size + 1),
  };
}

function KeyAttributeValuesList({
  instance,
  cards,
  onToggleValue,
}: {
  instance: AdvancedFilterCollectionInstance;
  cards: AdvancedFilterCard[];
  onToggleValue: (
    instance: AdvancedFilterCollectionInstance,
    value: string
  ) => void;
}) {
  const { t } = useTranslation();
  const { storefront_id } = useStoreState();
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery] = useDebounce(
    searchQuery,
    VALUES_SEARCH_DEBOUNCE_MS
  );
  const { values, isLoading, hasMore, loadMore } = useKeyAttributeValues(
    storefront_id,
    instance.id,
    debouncedSearchQuery
  );
  const { ref: sentinelRef, inView } = useInView({
    triggerOnce: false,
    rootMargin: "50px",
  });
  useEffect(() => {
    if (inView) loadMore();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView]);

  const addedValues = useMemo(
    () =>
      new Set(
        cards
          .filter((card) => card.collection_instance_id === instance.id)
          .map((card) => card.key_attribute_value)
      ),
    [cards, instance.id]
  );

  return (
    <ValuesSection>
      <FilterGroupSearchBarWrapper>
        <SearchBar
          placeHolder={t("Search values")}
          query={searchQuery}
          handleChange={(e) => setSearchQuery(e.target.value)}
          handleClearInput={() => setSearchQuery("")}
        />
      </FilterGroupSearchBarWrapper>
      <FilterGroupOptionsList>
        {isLoading && (
          <EmptyState>
            <Loader isLoading width={18} />
          </EmptyState>
        )}
        {!isLoading && values.length === 0 && (
          <EmptyState>
            <TextSmMedium color="secondary">
              {t("No values found")}
            </TextSmMedium>
          </EmptyState>
        )}
        {values.map(({ value, product_count }, index) => {
          const checkboxId = `advanced-filter-value-${instance.id}-${index}`;
          return (
            <li key={value}>
              <CheckBoxNoLabel
                id={checkboxId}
                data-testid={`advanced-filter-value-${instance.id}-${value}`}
                checked={addedValues.has(value)}
                onChange={() => onToggleValue(instance, value)}
              />
              <ValueLabel htmlFor={checkboxId}>
                <TextSmRegular color="secondary">{value}</TextSmRegular>
                <TextSmRegular color="tertiary">{product_count}</TextSmRegular>
              </ValueLabel>
            </li>
          );
        })}
        {hasMore && !isLoading && <div ref={sentinelRef} />}
      </FilterGroupOptionsList>
    </ValuesSection>
  );
}

type AdvancedFiltersBrowsePaneProps = {
  instances: AdvancedFilterCollectionInstance[];
  cards: AdvancedFilterCard[];
  onToggleValue: (
    instance: AdvancedFilterCollectionInstance,
    value: string
  ) => void;
};

export const AdvancedFiltersBrowsePane = ({
  instances,
  cards,
  onToggleValue,
}: AdvancedFiltersBrowsePaneProps) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");
  const [openKeyAttributeIds, setOpenKeyAttributeIds] = useState<Set<string>>(
    new Set()
  );

  const filteredInstances = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return instances;
    return instances.filter(
      (instance) =>
        instance.collection_name.toLowerCase().includes(query) ||
        instance.key_attribute_display_name.toLowerCase().includes(query)
    );
  }, [instances, searchQuery]);

  const toggleKeyAttribute = (id: string) => {
    const next = new Set(openKeyAttributeIds);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    setOpenKeyAttributeIds(next);
  };

  return (
    <PaneWrapper>
      <SearchBarWrapper>
        <SearchBar
          placeHolder={t("Search for filters")}
          query={searchQuery}
          handleChange={(e) => setSearchQuery(e.target.value)}
          handleClearInput={() => setSearchQuery("")}
        />
      </SearchBarWrapper>
      {filteredInstances.length === 0 && (
        <EmptyState>
          <TextSmMedium color="secondary">
            {t("No advanced filters configured")}
          </TextSmMedium>
        </EmptyState>
      )}
      {filteredInstances.map((instance) => {
        const keyAttributeOpen = openKeyAttributeIds.has(instance.id);
        return (
          <FilterGroupWrapper key={instance.id} expanded={keyAttributeOpen}>
            <GroupHeaderButton
              as="button"
              expanded={keyAttributeOpen}
              data-testid={`advanced-filter-key-attribute-${instance.id}`}
              onClick={() => toggleKeyAttribute(instance.id)}
            >
              <TextSmMedium color="secondary">
                {instance.collection_name}:{" "}
                <strong>{instance.key_attribute_display_name}</strong>
              </TextSmMedium>
              <AnimatedExpandToggle expanded={keyAttributeOpen} />
            </GroupHeaderButton>
            {keyAttributeOpen && (
              <KeyAttributeValuesList
                instance={instance}
                cards={cards}
                onToggleValue={onToggleValue}
              />
            )}
          </FilterGroupWrapper>
        );
      })}
    </PaneWrapper>
  );
};

AdvancedFiltersBrowsePane.displayName = "AdvancedFiltersBrowsePane";
